import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { groupAPI, authAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function Groups() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [groups, setGroups] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAddStudentsModal, setShowAddStudentsModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', isBatch: false });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  useEffect(() => {
    fetchGroups();
    fetchAllStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setGroups(response.data.groups);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch groups', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const response = await authAPI.getAllStudents(1, 1000); // Get all students for group management
      setAllStudents(response.data.students || []);
    } catch (error) {
      console.error('Failed to fetch students:', error);
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await groupAPI.updateGroup(editingGroup._id, formData);
        showToast('Group updated successfully', 'success');
      } else {
        await groupAPI.createGroup(formData);
        showToast('Group created successfully', 'success');
      }
      setFormData({ name: '', description: '', isBatch: false });
      setShowCreateModal(false);
      setEditMode(false);
      setEditingGroup(null);
      fetchGroups();
    } catch (error) {
      showToast(error.response?.data?.message || `Failed to ${editMode ? 'update' : 'create'} group`, 'error');
    }
  };

  const handleEditGroup = (group) => {
    setEditMode(true);
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || '',
      isBatch: group.isBatch || false
    });
    setShowCreateModal(true);
  };

  const handleAddStudents = async () => {
    if (selectedStudents.length === 0) {
      showToast('Please select at least one student', 'error');
      return;
    }

    try {
      await groupAPI.addStudents(selectedGroup._id, selectedStudents);
      showToast('Students added successfully', 'success');
      setSelectedStudents([]);
      setShowAddStudentsModal(false);
      fetchGroups();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to add students', 'error');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this group?')) return;

    try {
      await groupAPI.deleteGroup(groupId);
      showToast('Group deleted successfully', 'success');
      fetchGroups();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to delete group', 'error');
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudents(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const getAvailableStudents = () => {
    if (!selectedGroup) return allStudents;
    const groupStudentIds = selectedGroup.students.map(s => s._id);
    return allStudents.filter(s => !groupStudentIds.includes(s._id));
  };

  const filteredStudents = getAvailableStudents().filter(student =>
    student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#fff' }}>Loading groups...</p>
      </div>
    );
  }

  return (
    <>
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>👥 Student Groups</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              Manage student groups and assignments
            </p>
          </div>
          <button
            onClick={() => {
              setEditMode(false);
              setEditingGroup(null);
              setFormData({ name: '', description: '', isBatch: false });
              setShowCreateModal(true);
            }}
            className="btn btn-primary"
          >
            + Create Group
          </button>
        </div>

      {groups.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f7fafc',
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>👥</p>
          <h3 style={{ margin: '0 0 8px 0', color: '#2d3748' }}>No Groups Yet</h3>
          <p style={{ color: '#718096', margin: '0 0 20px 0' }}>Create your first student group to get started</p>
          <button
            onClick={() => {
              setEditMode(false);
              setEditingGroup(null);
              setFormData({ name: '', description: '', isBatch: false });
              setShowCreateModal(true);
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Create Group
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {groups.map(group => (
            <div
              key={group._id}
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <h3 
                      onClick={() => navigate(`/groups/${group._id}`)}
                      style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        color: '#667eea',
                        cursor: 'pointer',
                        textDecoration: 'none',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#5a67d8'}
                      onMouseLeave={(e) => e.target.style.color = '#667eea'}
                    >
                      {group.name} →
                    </h3>
                    {group.isBatch && (
                      <span style={{
                        padding: '3px 10px',
                        backgroundColor: '#f0f4ff',
                        color: '#667eea',
                        border: '1px solid #c7d2fe',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600',
                        letterSpacing: '0.5px',
                        textTransform: 'uppercase'
                      }}>
                        🎓 Batch
                      </span>
                    )}
                  </div>
                  {group.description && (
                    <p style={{ margin: '0 0 8px 0', color: '#718096', fontSize: '14px' }}>{group.description}</p>
                  )}
                  <p style={{ margin: 0, color: '#a0aec0', fontSize: '13px' }}>
                    {group.students.length} student{group.students.length !== 1 ? 's' : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEditGroup(group)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#fff',
                      color: '#4a5568',
                      border: '1.5px solid #cbd5e0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    onClick={() => {
                      setSelectedGroup(group);
                      setShowAddStudentsModal(true);
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#eef2ff',
                      color: '#667eea',
                      border: '1.5px solid #667eea',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    + Add Students
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group._id)}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#fff5f5',
                      color: '#c53030',
                      border: '1.5px solid #fc8181',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer'
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>

    {/* Create Group Modal */}
    {showCreateModal && (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1050,
          paddingTop: '80px',
          padding: '20px',
          overflow: 'auto'
        }}
        onClick={() => {
          setShowCreateModal(false);
          setFormData({ name: '', description: '', isBatch: false });
          setEditMode(false);
          setEditingGroup(null);
        }}
      >
        <div 
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
            <h2 style={{ margin: '0 0 20px 0', fontSize: '20px', color: '#2d3748' }}>
              {editMode ? 'Edit Group' : 'Create New Group'}
            </h2>
            <form onSubmit={handleCreateGroup}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Group Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., Math Class 2024"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#4a5568' }}>
                  Description (Optional)
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the group"
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1.5px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    resize: 'vertical'
                  }}
                />
              </div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#4a5568'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isBatch}
                    onChange={(e) => setFormData({ ...formData, isBatch: e.target.checked })}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer',
                      accentColor: '#667eea'
                    }}
                  />
                  <span style={{ fontWeight: '600' }}>Mark as Batch</span>
                </label>
                <p style={{ 
                  margin: '4px 0 0 26px', 
                  fontSize: '12px', 
                  color: '#718096' 
                }}>
                  Enable this to identify this group as a batch (e.g., 2024 cohort, semester group)
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setFormData({ name: '', description: '', isBatch: false });
                    setEditMode(false);
                    setEditingGroup(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#f7fafc',
                    color: '#4a5568',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editMode ? 'Update Group' : 'Create Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    {/* Add Students Modal */}
    {showAddStudentsModal && selectedGroup && (
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          zIndex: 1050,
          paddingTop: '80px',
          padding: '20px',
          overflow: 'auto'
        }}
        onClick={() => {
          setShowAddStudentsModal(false);
          setSelectedStudents([]);
          setSearchTerm('');
        }}
      >
        <div 
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#2d3748' }}>
              Add Students to "{selectedGroup.name}"
            </h2>

            {allStudents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>👨‍🎓</p>
                <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
                  No students registered yet. Students need to register first.
                </p>
              </div>
            ) : getAvailableStudents().length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>✓</p>
                <p style={{ color: '#718096', fontSize: '14px', margin: 0 }}>
                  All students are already in this group
                </p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <input
                    type="text"
                    placeholder="🔍 Search students..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
                  {filteredStudents.length === 0 ? (
                    <p style={{ color: '#718096', padding: '20px', textAlign: 'center' }}>
                      No students found
                    </p>
                  ) : (
                    filteredStudents.map(student => (
                      <label
                        key={student._id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          marginBottom: '8px',
                          backgroundColor: selectedStudents.includes(student._id) ? '#eef2ff' : '#f7fafc',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          border: `1.5px solid ${selectedStudents.includes(student._id) ? '#667eea' : '#e2e8f0'}`
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student._id)}
                          onChange={() => toggleStudentSelection(student._id)}
                          style={{ marginRight: '12px', width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontWeight: '600', color: '#2d3748', fontSize: '14px' }}>{student.name}</p>
                          <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>{student.email}</p>
                        </div>
                      </label>
                    ))
                  )}
                </div>

                {selectedStudents.length > 0 && (
                  <p style={{ marginBottom: '16px', color: '#667eea', fontSize: '14px', fontWeight: '600' }}>
                    {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </>
            )}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowAddStudentsModal(false);
                  setSelectedStudents([]);
                  setSearchTerm('');
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f7fafc',
                  color: '#4a5568',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                {allStudents.length === 0 || getAvailableStudents().length === 0 ? 'Close' : 'Cancel'}
              </button>
              {allStudents.length > 0 && getAvailableStudents().length > 0 && (
                <button
                  onClick={handleAddStudents}
                  disabled={selectedStudents.length === 0}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: selectedStudents.length === 0 ? '#cbd5e0' : '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: selectedStudents.length === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Add Selected
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Groups;
